import { GlossaryQuizQuestion } from '../types/glossaryQuiz'

export const GLOSSARY_QUIZ_QUESTIONS: GlossaryQuizQuestion[] = [
  {
    id: 'tq01',
    round: 'core10',
    category: 'core_terminology',
    term: 'Views',
    difficulty: 'easy',
    learningObjective: 'Identify what a View represents.',
    questionText: 'One quick warm-up: what does a View count?',
    options: [
      'One offer unit load',
      'One individual offer shown',
      'One click',
      'One conversion'
    ],
    correctOptionIndex: 0,
    explanation: 'Views count placement loads, not individual offers.',
    referenceSection: '#views'
  },
  {
    id: 'tq02',
    round: 'core10',
    category: 'core_terminology',
    term: 'Impressions',
    difficulty: 'easy',
    learningObjective: 'Identify what an Impression represents.',
    questionText: 'Ad detective time: when is an Impression counted?',
    options: [
      'When an offer/ad is viewed at least 50 percent',
      'When the page URL changes',
      'When a conversion postback arrives',
      'When a user logs in'
    ],
    correctOptionIndex: 0,
    explanation: 'Impressions use a viewability threshold, not just load/render events.',
    referenceSection: '#impressions'
  },
  {
    id: 'tq03',
    round: 'core10',
    category: 'core_terminology',
    term: 'Views vs Impressions',
    difficulty: 'easy',
    learningObjective: 'Understand the typical ratio guidance.',
    questionText: 'If one View had a social life, how many Impressions would it usually bring in MomentPerks?',
    options: [
      'Exactly 1, always',
      'Typically 1 to 5',
      'Typically 1 to 3 only',
      'Always 10+'
    ],
    correctOptionIndex: 1,
    explanation: 'Current glossary guidance: MomentPerks is typically 1 to 5; Perkswall can be higher.',
    referenceSection: '#impressions'
  },
  {
    id: 'tq04',
    round: 'core10',
    category: 'rate_metrics',
    term: 'CTR',
    difficulty: 'easy',
    learningObjective: 'Use the correct CTR formula.',
    questionText: 'No tricks, just math: which formula defines CTR in this glossary?',
    options: [
      'Clicks / Impressions',
      'Clicks / Views',
      'Conversions / Clicks',
      'Gross Revenue / Clicks'
    ],
    correctOptionIndex: 0,
    explanation: 'CTR is impressions-based in this glossary.',
    referenceSection: '#ctr-click-through-rate'
  },
  {
    id: 'tq05',
    round: 'core10',
    category: 'rate_metrics',
    term: 'CTR zero denominator',
    difficulty: 'easy',
    learningObjective: 'Interpret CTR behavior safely.',
    questionText: 'When Impressions = 0, what should CTR display to avoid nonsense math?',
    options: [
      '0 percent',
      '100 percent',
      'Blank',
      "Carry yesterday's value"
    ],
    correctOptionIndex: 2,
    explanation: 'Blank avoids misleading output when the denominator is zero.',
    referenceSection: '#ctr-click-through-rate'
  },
  {
    id: 'tq06',
    round: 'core10',
    category: 'yield_metrics',
    term: 'RPM (Views)',
    difficulty: 'easy',
    learningObjective: 'Use the correct denominator for RPM (Views).',
    questionText: 'Pick the correct formula for RPM (Views).',
    options: [
      '(Gross Revenue / Views) x 1,000',
      '(Gross Revenue / Impressions) x 1,000',
      '(Earnings / Clicks) x 1,000',
      '(Partner Revenue / Conversions) x 1,000'
    ],
    correctOptionIndex: 0,
    explanation: 'RPM (Views) uses Views as the denominator.',
    referenceSection: '#rpm-revenue-per-mille'
  },
  {
    id: 'tq07',
    round: 'core10',
    category: 'yield_metrics',
    term: 'RPM (Offers)',
    difficulty: 'easy',
    learningObjective: 'Use the correct denominator for RPM (Offers).',
    questionText: 'Which formula defines RPM (Offers)?',
    options: [
      '(Gross Revenue / Impressions) x 1,000',
      '(Gross Revenue / Views) x 1,000',
      '(Earnings / Views) x 1,000',
      '(Clicks / Impressions) x 1,000'
    ],
    correctOptionIndex: 0,
    explanation: 'Same revenue, different denominator: Impressions.',
    referenceSection: '#rpm-revenue-per-mille'
  },
  {
    id: 'tq08',
    round: 'core10',
    category: 'rate_metrics',
    term: 'Impressions Per View',
    difficulty: 'easy',
    learningObjective: 'Interpret offer density per load.',
    questionText: 'Which formula defines Impressions Per View?',
    options: [
      'Impressions / Views',
      'Views / Impressions',
      'Clicks / Views',
      'Conversions / Impressions'
    ],
    correctOptionIndex: 0,
    explanation: 'It measures average offer/ad views per placement load.',
    referenceSection: '#impressions-per-view'
  },
  {
    id: 'tq09',
    round: 'core10',
    category: 'volume_and_revenue',
    term: 'Partner Revenue',
    difficulty: 'easy',
    learningObjective: 'Identify Partner Revenue correctly.',
    questionText: 'Money minute: what is Partner Revenue?',
    options: [
      'Total brand spend',
      'Your payout per conversion, summed across conversions',
      'Your CPC charges',
      'Gross revenue minus clicks'
    ],
    correctOptionIndex: 1,
    explanation: 'Partner Revenue is payout, not total generated value.',
    referenceSection: '#partner-revenue'
  },
  {
    id: 'tq10',
    round: 'core10',
    category: 'volume_and_revenue',
    term: 'Partner Cost',
    difficulty: 'easy',
    learningObjective: 'Identify Partner Cost correctly.',
    questionText: 'Cost check: Partner Cost is best described as...',
    options: [
      'Sum of CPC costs across clicks',
      'Sum of conversion payouts',
      'Gross revenue from conversions',
      'Revenue per 1,000 impressions'
    ],
    correctOptionIndex: 0,
    explanation: 'Partner Cost tracks CPC costs tied to click volume.',
    referenceSection: '#partner-cost'
  },
  {
    id: 'tq11',
    round: 'bonus15',
    category: 'identity_and_privacy',
    term: 'Unique Visitors',
    difficulty: 'easy',
    learningObjective: 'Apply privacy-safe ID usage.',
    questionText: 'Privacy pop quiz: what must be true about pub_user_id for Unique Visitors tracking?',
    options: [
      'It can include full email and phone',
      'It must be anonymized, non-PII, and stable',
      'It should rotate every page load',
      'It is optional for all use cases'
    ],
    correctOptionIndex: 1,
    explanation: 'No PII in pub_user_id. Use a stable anonymized identifier.',
    referenceSection: '#unique-visitors'
  },
  {
    id: 'tq12',
    round: 'bonus15',
    category: 'dashboard_concepts',
    term: 'Reporting API impressions field',
    difficulty: 'medium',
    learningObjective: 'Understand glossary/API naming mismatch.',
    questionText: 'Friendly trap alert: in Reporting API responses, the field named impressions maps to what dashboard metric?',
    options: [
      'Impressions',
      'Views',
      'Clicks',
      'Conversions'
    ],
    correctOptionIndex: 1,
    explanation: 'The glossary notes this naming inconsistency explicitly.',
    referenceSection: '#reporting-api'
  },
  {
    id: 'tq13',
    round: 'bonus15',
    category: 'core_terminology',
    term: 'Clicks',
    difficulty: 'easy',
    learningObjective: 'Clarify click event counting.',
    questionText: 'Button mashing is not required, but what does Clicks count?',
    options: [
      'Each positive CTA click/tap',
      'Each view',
      'Each impression',
      'Each conversion'
    ],
    correctOptionIndex: 0,
    explanation: 'Clicks are positive CTA interaction events.',
    referenceSection: '#clicks'
  },
  {
    id: 'tq14',
    round: 'bonus15',
    category: 'core_terminology',
    term: 'Conversions',
    difficulty: 'easy',
    learningObjective: 'Clarify conversion counting.',
    questionText: 'What event turns into a Conversion in this glossary?',
    options: [
      'Offer rendered',
      'User completed advertiser action confirmed via postback',
      'CTA click',
      'Theme saved'
    ],
    correctOptionIndex: 1,
    explanation: 'Conversions are advertiser-defined actions confirmed by postback.',
    referenceSection: '#conversions'
  },
  {
    id: 'tq15',
    round: 'bonus15',
    category: 'rate_metrics',
    term: 'CVR',
    difficulty: 'easy',
    learningObjective: 'Use the CVR formula.',
    questionText: 'Which formula defines CVR?',
    options: [
      'Conversions / Clicks',
      'Clicks / Impressions',
      'Earnings / Clicks',
      'Revenue / Views'
    ],
    correctOptionIndex: 0,
    explanation: 'CVR measures how many clicks turned into conversions.',
    referenceSection: '#cvr-conversion-rate'
  },
  {
    id: 'tq16',
    round: 'bonus15',
    category: 'per_event_metrics',
    term: 'EPC',
    difficulty: 'easy',
    learningObjective: 'Use the EPC formula.',
    questionText: 'EPC is short for Earnings Per Click. Which formula is correct?',
    options: [
      'Earnings / Clicks',
      'Gross Revenue / Clicks',
      'Partner Revenue / Clicks',
      'Conversions / Clicks'
    ],
    correctOptionIndex: 0,
    explanation: 'EPC uses Earnings as the numerator.',
    referenceSection: '#epc-earnings-per-click'
  },
  {
    id: 'tq17',
    round: 'bonus15',
    category: 'per_event_metrics',
    term: 'RPC',
    difficulty: 'easy',
    learningObjective: 'Use the RPC formula.',
    questionText: 'Which formula defines RPC (Revenue Per Click)?',
    options: [
      'Gross Revenue / Clicks',
      'Partner Revenue / Clicks',
      'Earnings / Clicks',
      'Conversions / Clicks'
    ],
    correctOptionIndex: 0,
    explanation: 'RPC uses Gross Revenue divided by Clicks.',
    referenceSection: '#rpc-revenue-per-click'
  },
  {
    id: 'tq18',
    round: 'bonus15',
    category: 'per_event_metrics',
    term: 'RPT',
    difficulty: 'easy',
    learningObjective: 'Use the RPT formula.',
    questionText: 'RPT is revenue per what, exactly?',
    options: [
      'Per conversion: Gross Revenue / Conversions',
      'Per click: Gross Revenue / Clicks',
      'Per impression: Gross Revenue / Impressions',
      'Per view: Gross Revenue / Views'
    ],
    correctOptionIndex: 0,
    explanation: 'RPT is revenue per conversion (transaction).',
    referenceSection: '#rpt-revenue-per-transaction'
  },
  {
    id: 'tq19',
    round: 'bonus15',
    category: 'yield_metrics',
    term: 'eRPM (Views)',
    difficulty: 'medium',
    learningObjective: 'Use the eRPM formula.',
    questionText: 'Which formula defines eRPM (Views)?',
    options: [
      '(Earnings / Views) x 1,000',
      '(Gross Revenue / Views) x 1,000',
      '(Earnings / Clicks) x 1,000',
      '(Partner Revenue / Views) x 1,000'
    ],
    correctOptionIndex: 0,
    explanation: 'eRPM is earnings per 1,000 views.',
    referenceSection: '#erpm-earnings-rpm'
  },
  {
    id: 'tq20',
    round: 'bonus15',
    category: 'rate_metrics',
    term: 'Fill Rate',
    difficulty: 'medium',
    learningObjective: 'Use the Fill Rate formula.',
    questionText: 'Fill Rate asks: of all Views, how many had at least one impression? Which formula matches?',
    options: [
      'Views with >=1 impression / Total Views',
      'Impressions / Views',
      'Clicks / Impressions',
      'Conversions / Clicks'
    ],
    correctOptionIndex: 0,
    explanation: 'Fill Rate is a coverage metric over view loads.',
    referenceSection: '#fill-rate'
  },
  {
    id: 'tq21',
    round: 'bonus15',
    category: 'dashboard_concepts',
    term: 'Demand Tab',
    difficulty: 'easy',
    learningObjective: 'Differentiate dashboard tabs.',
    questionText: 'Demand Tab shows performance from which offer source?',
    options: [
      'MomentScience sourced (network) offers',
      'Direct offers only',
      'Only endemic offers',
      'Only archived offers'
    ],
    correctOptionIndex: 0,
    explanation: 'Demand Tab is the network-sourced side.',
    referenceSection: '#demand-tab'
  },
  {
    id: 'tq22',
    round: 'bonus15',
    category: 'dashboard_concepts',
    term: 'Direct Tab',
    difficulty: 'easy',
    learningObjective: 'Differentiate dashboard tabs.',
    questionText: 'Direct Tab mirrors Demand Tab, with what extra financial context called out?',
    options: [
      'Partner Cost factored in',
      'Only gross margin',
      'No conversions shown',
      'No click metrics'
    ],
    correctOptionIndex: 0,
    explanation: 'Direct Tab explicitly factors Partner Cost.',
    referenceSection: '#direct-tab'
  },
  {
    id: 'tq23',
    round: 'bonus15',
    category: 'dashboard_concepts',
    term: 'Campaign Details',
    difficulty: 'medium',
    learningObjective: 'Recognize campaign-level columns.',
    questionText: 'Which revenue pair appears in Campaign Details columns?',
    options: [
      'Partner Revenue and Partner Cost',
      'Gross Revenue and Earnings',
      'RPT and EPC',
      'CPA and CPS'
    ],
    correctOptionIndex: 0,
    explanation: 'Campaign Details lists partner-facing revenue and cost fields.',
    referenceSection: '#campaign-details'
  },
  {
    id: 'tq24',
    round: 'bonus15',
    category: 'core_terminology',
    term: 'MomentPerks API naming',
    difficulty: 'medium',
    learningObjective: 'Use canonical terminology.',
    questionText: 'Legacy docs may say Moments API. What is the canonical term in this glossary?',
    options: [
      'MomentPerks API',
      'Network API',
      'Offer Engine API',
      'Campaign API'
    ],
    correctOptionIndex: 0,
    explanation: 'The glossary standardizes on MomentPerks API.',
    referenceSection: '#momentperks-api'
  },
  {
    id: 'tq25',
    round: 'bonus15',
    category: 'dashboard_concepts',
    term: 'Sequence Rules',
    difficulty: 'medium',
    learningObjective: 'Recall rule-position scope.',
    questionText: 'Sequence Rules control which offers appear at which positions in a MomentPerks response. What position range is documented?',
    options: [
      '1 to 5',
      '1 to 3',
      '1 to 10',
      '0 to 5'
    ],
    correctOptionIndex: 0,
    explanation: 'The glossary documents position range 1 to 5.',
    referenceSection: '#sequence-rules'
  }
]
